/**
 * Gestão de Recursos - Dashboard Overview Page
 *
 * Main dashboard page for resource management showing aggregated data across projects.
 * This is the landing page for /financeiro/gestao-recursos-dashboard route.
 *
 * Layout from Figma (node 2925-6718):
 * - Header: Project selector dropdown
 * - 3 Balance summary cards in top row
 * - 2-column layout:
 *   - Left column (wider): Statistics chart + Transactions table
 *   - Right column (narrower): 3 compact charts
 *
 * Features:
 * - Project selector (switch between projects)
 * - Balance summary cards (Recursos Captados, Saldo a Captar, Total)
 * - Monthly statistics bar chart (Completos vs Pendentes)
 * - Transactions table with search, filter, pagination, copy
 * - 3 compact charts (Saldo, Recursos, Investimentos)
 *
 * Navigation:
 * - Clicking a project in the selector loads its detail page
 * - Table rows can link to detail views (future enhancement)
 */

import { useLocation } from "wouter";
import { AppContainer } from "@/components/layout/app-container";
import { Button } from "@/components/ui/button";
import { Plus, FileWarning } from "lucide-react";
import { ProjectSelector } from "@/components/gestao-recursos/project-selector";
import { BalanceSummarySection } from "@/components/gestao-recursos/balance-summary-section";
import { DashboardStatisticsChart } from "@/components/gestao-recursos/dashboard-statistics-chart";
import { ResourcesTransactionsTable } from "@/components/gestao-recursos/resources-transactions-table";
import { DashboardCompactChart } from "@/components/gestao-recursos/dashboard-compact-chart";
import { Skeleton } from "@/components/ui/skeleton";
import { useRecursosDashboard } from "@/services/queries/useRecursos";
import { ProjectOption } from "@/types/recursos-dashboard.interface";
import { useProjetoContext } from "@/context/projeto";

export default function GestaoRecursosDashboard() {
  const [, setLocation] = useLocation();

  // Get selected project from context
  const { projetoSelecionado: contextProject, selecionarProjeto: setSelectedProject, projetos: projects } = useProjetoContext();
  const selectedProjectId = contextProject?.id?.toString() ?? '';

  // Fetch data from API
  const { data: apiData, isLoading, isError } = useRecursosDashboard(selectedProjectId);

  // Get API data - no mock fallback
  const dashboardData = apiData;

  // Check if we have meaningful data
  const hasData = dashboardData?.data &&
    dashboardData.data.balanceSummary;

  // Use API data or empty defaults
  const data = dashboardData?.data;

  // Get current project data
  const selectedProject =
    data?.availableProjects?.find(
      (p: ProjectOption) => p.id === selectedProjectId
    ) || contextProject || data?.selectedProject;

  // Handlers
  const handleProjectChange = (projectId: string) => {
    // Update project in context - API hook will automatically refetch data
    const project = projects.find((p) => p.id.toString() === projectId);
    if (project) {
      setSelectedProject(project);
    }
  };

  const handleGoToBudget = () => {
    // Navigate to budget page
    setLocation("/financeiro/gestao-recursos");
  };

  const handleCreateNew = () => {
    // Navigate to create project page
    setLocation("/cadastrar-projeto");
  };

  // Show message if no project is selected
  if (!selectedProjectId) {
    return (
      <AppContainer
        title="Gestão de Recursos"
        description="Visão geral dos recursos e transações do projeto"
      >
        <div className="flex items-center justify-center h-64 text-gray-400">
          Selecione um projeto para visualizar o dashboard
        </div>
      </AppContainer>
    );
  }

  // Show loading skeleton
  if (isLoading) {
    return (
      <AppContainer
        title="Gestão de Recursos"
        description="Visão geral dos recursos e transações do projeto"
      >
        <div className="flex flex-col gap-[27px]">
          <Skeleton className="h-[60px] w-[300px] rounded-lg" />
          <div className="flex gap-[30px]">
            <div className="w-[718px] flex flex-col gap-[24px]">
              <Skeleton className="h-[150px] w-full rounded-lg" />
            </div>
            <div className="flex-1 flex flex-col gap-[27px]">
              <Skeleton className="h-[300px] w-full rounded-lg" />
              <Skeleton className="h-[400px] w-full rounded-lg" />
            </div>
            <div className="w-[428px] flex flex-col gap-[31px]">
              <Skeleton className="h-[200px] w-full rounded-lg" />
              <Skeleton className="h-[200px] w-full rounded-lg" />
              <Skeleton className="h-[200px] w-full rounded-lg" />
            </div>
          </div>
        </div>
      </AppContainer>
    );
  }

  // Show error message
  if (isError) {
    return (
      <AppContainer
        title="Gestão de Recursos"
        description="Visão geral dos recursos e transações do projeto"
      >
        <div className="flex items-center justify-center h-64 text-red-400">
          Erro ao carregar os dados do dashboard. Tente novamente.
        </div>
      </AppContainer>
    );
  }

  // Show empty state if no data exists
  if (!hasData) {
    return (
      <AppContainer
        title="Gestão de Recursos"
        description="Visão geral dos recursos e transações do projeto"
        button={
          <>
            <Button
              onClick={handleGoToBudget}
              variant="outline"
              size="sm"
              className="border-[#7846FF] text-[#7846FF] hover:bg-[#7846FF] hover:text-white"
            >
              Ir para Orçamentos
            </Button>
            <Button
              onClick={handleCreateNew}
              size="sm"
              className="bg-[#7846FF] hover:bg-[#9b6fff] text-[#F5F2FF]"
            >
              <Plus size={16} />
              Criar Novo
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {/* Project Selector */}
          <div className="flex justify-start">
            <ProjectSelector
              projects={data!.availableProjects}
              selectedProject={selectedProject as ProjectOption}
              onProjectChange={handleProjectChange}
            />
          </div>

          {/* Empty State */}
          <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3 bg-[#1A1625] rounded-lg">
            <FileWarning size={48} className="text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-300">Nenhum dado financeiro encontrado</h3>
            <p className="text-center max-w-md text-sm">
              Este projeto ainda não possui dados de recursos, transações ou orçamento cadastrados.
              <br />
              Acesse a página de orçamentos para começar a adicionar informações.
            </p>
            <Button
              onClick={handleGoToBudget}
              size="sm"
              className="mt-2 bg-[#7846FF] hover:bg-[#9b6fff] text-white"
            >
              Ir para Gestão de Recursos
            </Button>
          </div>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer
      title="Gestão de Recursos"
      description="Visão geral dos recursos e transações do projeto"
      button={
        <>
          <Button
            onClick={handleGoToBudget}
            variant="outline"
            size="sm"
            className="border-[#7846FF] text-[#7846FF] hover:bg-[#7846FF] hover:text-white"
          >
            Ir para Orçamentos
          </Button>
          <Button
            onClick={handleCreateNew}
            size="sm"
            className="bg-[#7846FF] hover:bg-[#9b6fff] text-[#F5F2FF]"
          >
            <Plus size={16} />
            Criar Novo
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Project Selector */}
        <div className="flex justify-start">
          <ProjectSelector
            projects={data!.availableProjects}
            selectedProject={selectedProject as ProjectOption}
            onProjectChange={handleProjectChange}
          />
        </div>

        {/* Main Content - 3 Column Layout */}
        <div className="flex gap-4">
          {/* Left Column - Balance Cards */}
          <div className="w-[450px] xl:w-[550px] flex flex-col gap-4">
            <BalanceSummarySection
              balanceSummary={data!.balanceSummary}
            />
          </div>

          {/* Middle Column - Statistics Chart + Transactions Table */}
          <div className="flex-1 flex flex-col gap-4">
            {/* Monthly Statistics Bar Chart */}
            <DashboardStatisticsChart
              data={data!.monthlyStats}
              period="Últimos 7 meses"
              isLoading={isLoading}
            />

            {/* Transactions Table */}
            <ResourcesTransactionsTable
              transactions={data!.transactions}
              isLoading={isLoading}
            />
          </div>

          {/* Right Column - Compact Charts */}
          <div className="w-[300px] xl:w-[350px] flex flex-col gap-4">
            {/* Balanço Chart */}
            <DashboardCompactChart
              data={data!.compactCharts.saldoCaptar}
            />

            {/* Investimento Chart */}
            <DashboardCompactChart
              data={data!.compactCharts.recursosCaptados}
            />

            {/* Balanço Chart */}
            <DashboardCompactChart
              data={data!.compactCharts.investimentos}
            />
          </div>
        </div>
      </div>
    </AppContainer>
  );
}
